import type {
  Asset,
  AudioReport,
  CreateAssetInput,
  CreateCurriculumChapterDto,
  CurriculumChapter,
  GenerateTeachingEvent,
  LessonPack,
  LessonPackBoard,
  LessonPackBundle,
  LessonPackInput,
  LessonPackListItem,
  LessonPackOp,
  LessonPackStatus,
  LessonPackStatusResponse,
  LessonPackSubject,
  Row,
  Script,
  ScriptListItem,
  ScriptOp,
  Slide,
  UpdateAssetInput,
  UpdateCurriculumChapterDto,
} from "./schema.types";

const API_BASE = process.env.PUBLIC_API_BASE ?? "http://localhost:4000/ai-tutor/ws";

// Bypass ngrok-free.app's HTML browser-warning interstitial (it strips CORS
// headers from the response). Only sent when the API base actually points at
// an ngrok tunnel — a leftover from tunnel-based dev, harmless but noisy
// against real deployments.
const NGROK_HEADERS: Record<string, string> = API_BASE.includes("ngrok")
  ? { "ngrok-skip-browser-warning": "true" }
  : {};

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...NGROK_HEADERS,
      ...(init?.headers ?? {}),
    },
    cache: "no-store",
  });
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text();
    }
    const message =
      typeof body === "object" && body && "message" in body
        ? String((body as { message: unknown }).message)
        : `API ${res.status}`;
    throw new ApiError(res.status, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  scripts: {
    list: () => request<ScriptListItem[]>("/scripts"),
    create: (label?: string) =>
      request<Script>("/scripts", {
        method: "POST",
        body: JSON.stringify({ label }),
      }),
    get: (id: string) => request<Script>(`/scripts/${id}`),
    replace: (id: string, payload: Partial<Script>) =>
      request<Script>(`/scripts/${id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      }),
    applyOps: (id: string, ops: ScriptOp[]) =>
      request<Script>(`/scripts/${id}/ops`, {
        method: "PATCH",
        body: JSON.stringify({ ops }),
      }),
    remove: (id: string) =>
      request<{ id: string; deleted: boolean }>(`/scripts/${id}`, {
        method: "DELETE",
      }),
  },
  lessonPacks: {
    list: (query?: {
      grade?: string;
      subject?: string;
      board?: string;
      status?: LessonPackStatus;
    }) => {
      const params = new URLSearchParams();
      Object.entries(query ?? {}).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
      });
      const qs = params.toString();
      return request<LessonPackListItem[]>(
        `/lesson-packs${qs ? `?${qs}` : ""}`,
      );
    },
    /** Async generate. Returns immediately with `pending` id; poll `status()`. */
    generate: (input: LessonPackInput) =>
      request<{ id: string; status: LessonPackStatus }>("/lesson-packs", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    /** Create an empty, hand-authored pack (no AI). Returns `done` immediately
     *  with one starter slide — open it straight in the editor. */
    createBlank: (input: {
      title: string;
      board: LessonPackBoard;
      grade: string;
      subject: LessonPackSubject;
      chapter?: string;
      designSystemId?: string;
    }) =>
      request<{ id: string; status: LessonPackStatus }>("/lesson-packs/blank", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    /** Lightweight poll — does not include slides. */
    status: (id: string) =>
      request<LessonPackStatusResponse>(`/lesson-packs/${id}/status`),
    /** Full document including slides + beatMeta. Used by the editor. */
    get: (id: string) => request<LessonPack>(`/lesson-packs/${id}`),
    /** Kick off (background) narration-audio generation. `mode: 'failed'` only
     *  fills steps that still lack a clip; default re-generates all. Returns
     *  immediately — poll `audioReport()` for progress. */
    generateAudio: (id: string, mode: "all" | "failed" = "all") =>
      request<{ started: boolean }>(
        `/lesson-packs/${id}/audio${mode === "failed" ? "?mode=failed" : ""}`,
        { method: "POST" },
      ),
    /** Poll narration-audio progress (succeeded/total + failed segments). */
    audioReport: (id: string) =>
      request<AudioReport>(`/lesson-packs/${id}/audio`),
    /** Apply edit ops (20 slide ops + 3 beat-meta ops). Reverts publishState
     *  to `draft` if it was `published`. */
    applyOps: (id: string, ops: LessonPackOp[]) =>
      request<LessonPack>(`/lesson-packs/${id}/ops`, {
        method: "PATCH",
        body: JSON.stringify({ ops }),
      }),
    publish: (id: string) =>
      request<LessonPack>(`/lesson-packs/${id}/publish`, { method: "POST" }),
    unpublish: (id: string) =>
      request<LessonPack>(`/lesson-packs/${id}/unpublish`, { method: "POST" }),
    /** Electron offline payload — only valid when `publishState === 'published'`. */
    bundle: (id: string) =>
      request<LessonPackBundle>(`/lesson-packs/${id}/bundle`),
    /** Re-expand `beats` → `slides`. Refuses if slides exist unless `force`. */
    materialize: (id: string, force = false) =>
      request<LessonPack>(
        `/lesson-packs/${id}/materialize${force ? "?force=true" : ""}`,
        { method: "POST" },
      ),
    remove: (id: string) =>
      request<void>(`/lesson-packs/${id}`, { method: "DELETE" }),
  },
  /**
   * Curriculum catalogue — drives the cascading
   * Board → Class → Subject → Chapter → Topic picker on the Generate
   * Lesson Pack form, plus the Curriculum admin page.
   * Contract: ai-tutor-be-main/docs/CURRICULUM_API.md.
   */
  curriculum: {
    /** `GET /curriculum/boards` → `["CBSE", …]`. Long-TTL on the FE. */
    listBoards: () => request<string[]>("/curriculum/boards"),

    /** `GET /curriculum/classes?board=…` → `[6, 7, 8, …]` (numeric, sorted). */
    listClasses: (query: { board?: string }) => {
      const params = new URLSearchParams();
      if (query.board) params.set("board", query.board);
      const qs = params.toString();
      return request<number[]>(`/curriculum/classes${qs ? `?${qs}` : ""}`);
    },

    /** `GET /curriculum/subjects?board=…&class=…` → free-form textbook names. */
    listSubjects: (query: { board?: string; class?: number }) => {
      const params = new URLSearchParams();
      if (query.board) params.set("board", query.board);
      if (typeof query.class === "number") params.set("class", String(query.class));
      const qs = params.toString();
      return request<string[]>(`/curriculum/subjects${qs ? `?${qs}` : ""}`);
    },

    /**
     * `GET /curriculum/chapters?board=…&class=…&subject=…` → array of FULL
     * chapter docs (incl. embedded `topics[]`) sorted by `displayOrder` on
     * the server. Always re-sort defensively in the UI by `displayOrder`,
     * NOT by `parseInt(chapterNumber)` (see CURRICULUM_API.md §5 rule 4).
     */
    listChapters: (query: { board?: string; class?: number; subject?: string }) => {
      const params = new URLSearchParams();
      if (query.board) params.set("board", query.board);
      if (typeof query.class === "number") params.set("class", String(query.class));
      if (query.subject) params.set("subject", query.subject);
      const qs = params.toString();
      return request<CurriculumChapter[]>(
        `/curriculum/chapters${qs ? `?${qs}` : ""}`,
      );
    },

    /** `GET /curriculum/chapters/:id` → one full chapter (incl. `topics[]`). */
    getChapter: (id: string) =>
      request<CurriculumChapter>(`/curriculum/chapters/${id}`),

    /** `POST /curriculum/chapters` — manual create. Used by the admin page
     *  when an extraction missed a chapter. */
    createChapter: (body: CreateCurriculumChapterDto) =>
      request<CurriculumChapter>("/curriculum/chapters", {
        method: "POST",
        body: JSON.stringify(body),
      }),

    /**
     * `PATCH /curriculum/chapters/:id` — partial update. Only fields you
     * include get written; omitted fields are LEFT ALONE (the backend
     * won't `$unset` keys you didn't touch — see CURRICULUM_API.md §3).
     * Sending `topics` REPLACES the whole array.
     */
    updateChapter: (id: string, patch: UpdateCurriculumChapterDto) =>
      request<CurriculumChapter>(`/curriculum/chapters/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),

    /** `DELETE /curriculum/chapters/:id` → the deleted document (HTTP 200). */
    deleteChapter: (id: string) =>
      request<CurriculumChapter>(`/curriculum/chapters/${id}`, {
        method: "DELETE",
      }),
  },
  uploads: {
    /**
     * Asks the backend for a fresh S3 signed URL plus the public hosted URL
     * we should persist on the script. Use `uploadFile()` if you also need
     * to push the bytes — it wraps both calls.
     */
    getSignedUrl: (input: {
      type?: string;
      name: string;
      extension: string;
      contentType: string;
    }) => {
      const params = new URLSearchParams();
      Object.entries(input).forEach(([k, v]) => {
        if (v !== undefined && v !== null) params.set(k, String(v));
      });
      return request<{
        url: string;
        uploadUrl: string;
        fileId?: string;
        key: string;
        baseUrl: string;
        name?: string;
        contentType?: string;
      }>(`/uploads/signed-url?${params.toString()}`);
    },
  },
  designSystems: {
    list: () => request<import("./schema.types").DesignSystemSummary[]>("/design-systems"),
    get: (id: string) => request<import("./schema.types").DesignSystemFull>(`/design-systems/${id}`),
    remove: (id: string) =>
      request<void>(`/design-systems/${id}`, { method: "DELETE" }),
  },

  assets: {
    /** `GET /assets?kind=model3d|gif&search=…&tags=a,b` → library assets, newest first. */
    list: (query?: { kind?: "model3d" | "gif"; search?: string; tags?: string[] }) => {
      const params = new URLSearchParams();
      if (query?.kind) params.set("kind", query.kind);
      if (query?.search) params.set("search", query.search);
      (query?.tags ?? []).forEach((t) => params.append("tags", t));
      const qs = params.toString();
      return request<Asset[]>(`/assets${qs ? `?${qs}` : ""}`);
    },

    /** `GET /assets/:id` → one library asset. */
    get: (id: string) => request<Asset>(`/assets/${id}`),

    /** `POST /assets` — register an uploaded model in the library. */
    create: (body: CreateAssetInput) =>
      request<Asset>("/assets", {
        method: "POST",
        body: JSON.stringify(body),
      }),

    /** `PATCH /assets/:id` — rename / retag / set default camera pose. */
    update: (id: string, patch: UpdateAssetInput) =>
      request<Asset>(`/assets/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),

    /** `DELETE /assets/:id` → the deleted document (HTTP 200). */
    remove: (id: string) =>
      request<Asset>(`/assets/${id}`, {
        method: "DELETE",
      }),
  },
  ai: {
    generateSlide: (input: {
      prompt?: string;
      topic?: string;
      subject?: string;
      tone?: string;
    }) =>
      request<Slide>("/ai/generate-slide", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    generateRow: (input: { prompt?: string; topic?: string }) =>
      request<Row>("/ai/generate-row", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    suggestNextSlide: (input: {
      scriptLabel?: string;
      metadata?: {
        subject?: string;
        chapter?: string;
        topic?: string;
        classLevel?: string;
        exam?: string;
        language?: string;
        tone?: string;
        style?: string;
      };
      existingSlides?: {
        label?: string;
        summary?: string;
        narrations?: string[];
      }[];
      userHint?: string;
      includeImage?: boolean;
      generateAudio?: boolean;
      voiceModel?: string;
      engine?: string;
    }) =>
      request<{
        slide: Slide;
        spec: {
          label: string;
          purpose: string;
          title: string;
          body: string;
          bullets: string[];
          imagePrompt: string;
          narration: string;
        };
        model: string;
        imageProvider?: "gemini" | "url-template";
      }>("/ai/suggest-next-slide", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    /** Strip an image's background via the backend image model. Returns a
     *  freshly hosted PNG URL (same subject on transparency). */
    removeImageBackground: (input: { imageUrl: string }) =>
      request<{ url: string; contentType: string; model: string }>(
        "/ai/remove-image-background",
        { method: "POST", body: JSON.stringify(input) },
      ),
    generateImage: (input: {
      prompt: string;
      width?: number;
      height?: number;
      aspectRatio?:
        | "1:1"
        | "16:9"
        | "9:16"
        | "4:3"
        | "3:4"
        | "3:2"
        | "2:3"
        | "5:4"
        | "4:5"
        | "21:9";
      imageSize?: "1K" | "2K";
      model?: string;
      style?: string;
    }) =>
      request<{
        url: string;
        prompt: string;
        width: number;
        height: number;
        model: string;
        contentType: string;
        provider: "gemini" | "url-template";
      }>("/ai/generate-image", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    generateNarrationText: (input: {
      slideLabel?: string;
      stepLabel?: string;
      elements?: { type: string; summary: string }[];
      previousNarrations?: string[];
      tone?: string;
      language?: string;
      targetSeconds?: number;
      customPrompt?: string;
      currentDraft?: string;
      rewriteInstruction?: string;
    }) =>
      request<{ text: string; model: string }>("/ai/generate-narration-text", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    generateNarration: (input: {
      text: string;
      voiceModel?: string;
      playbackSpeed?: number;
      engine?: string;
      lessonId?: string;
    }) =>
      request<{
        id: string;
        src: string;
        mimeType?: string;
        text: string;
        /** Devanagari sent to the VoxCPM model (custom engine only). */
        devnagariText?: string;
        durationMs: number;
        voiceModel: string;
        playbackSpeed: number;
        engine?: string;
        source: "tts";
        /** Per-word alignment timestamps (seconds) — present for the align TTS
         *  (VoxCPM2 via RunPod). Absent for engines without word alignment. */
        words?: { word: string; start: number; end: number }[];
      }>("/ai/generate-narration", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    /** Re-voice a source recording (record/upload) in a target voice. Returns a
     *  hosted MP3 + per-word timestamps, same shape the timeline word ruler uses. */
    modulateVoice: (input: {
      /** Base64 of the source recording (no data: prefix). */
      audioBase64: string;
      mimeType?: string;
      text?: string;
      voiceId?: string;
    }) =>
      request<{
        id: string;
        src: string;
        durationMs?: number;
        mime?: string;
        words?: { word: string; start: number; end: number }[];
        source: "voice-modulation";
      }>("/ai/modulate-voice", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    answerDoubt: (input: {
      scriptId: string;
      revealStepId: string;
      doubt: string;
      language?: string;
      tone?: string;
      generateAudio?: boolean;
      voiceModel?: string;
      engine?: string;
      targetSeconds?: number;
    }) =>
      request<{
        narration: string;
        bridge: string | null;
        visual: null | {
          kind: "text" | "latex" | "callout";
          content: string;
          rationale?: string;
        };
        audio?: {
          src: string;
          mimeType: string;
          durationMs: number;
          voiceModel: string;
          engine: string;
        };
        context: {
          scriptId: string;
          scriptLabel: string;
          slideId: string;
          slideLabel: string;
          stepId: string;
          stepLabel?: string;
          upcomingStepLabels: string[];
        };
        model: string;
        latencyMs: number;
      }>("/ai/answer-doubt", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    answerDoubtStream: (
      input: {
        scriptId: string;
        revealStepId: string;
        doubt: string;
        language?: string;
        tone?: string;
        generateAudio?: boolean;
        voiceModel?: string;
        engine?: string;
        targetSeconds?: number;
      },
      options?: { signal?: AbortSignal },
    ) =>
      streamNdjson<DoubtStreamChunk>("/ai/answer-doubt/stream", input, options),

    generateScriptStream: (
      input: {
        label?: string;
        metadata?: {
          subject?: string;
          chapter?: string;
          topic?: string;
          classLevel?: string;
          exam?: string;
          language?: string;
          tone?: string;
          style?: string;
        };
        userHint?: string;
        slideCount?: number;
        includeImages?: boolean;
        generateAudio?: boolean;
        voiceModel?: string;
        engine?: string;
        scriptId?: string;
      },
      options?: { signal?: AbortSignal },
    ) =>
      streamNdjson<GenerateScriptEvent>(
        "/ai/generate-script/stream",
        input,
        options,
      ),

    /** Pedagogical icon suggestions from the stable backend registry. */
    suggestIcons: (input: {
      context: string;
      slideContext?: string;
      max?: number;
      categories?: string[];
    }) =>
      request<{ suggestions: { id: string; reason?: string }[] }>(
        "/ai/suggest-icons",
        { method: "POST", body: JSON.stringify(input) },
      ),

    /**
     * Stream a per-slide teaching plan (reveal order + narration text +
     * emphasis cues). AUDIO-FREE — audio stays on the VoxCPM flow.
     */
    generateTeachingStream: (
      input: {
        slideLabel?: string;
        slideId?: string;
        scriptId?: string;
        metadata?: Record<string, unknown>;
        elements: { id: string; type: string; summary: string }[];
        options: {
          style?: string;
          persona?: string;
          socratic?: boolean;
          language?: string;
          tone?: string;
          targetSeconds?: number;
          userHint?: string;
        };
      },
      options?: { signal?: AbortSignal },
    ) =>
      streamNdjson<GenerateTeachingEvent>(
        "/ai/generate-teaching/stream",
        input,
        options,
      ),
  },
};

export { ApiError };

// ---------------------------------------------------------------------------
// Doubt streaming
// ---------------------------------------------------------------------------

export enum StreamChunkStatus {
  InProgress = "inprogress",
  Done = "done",
}
export enum StreamChunkType {
  Text = "text",
  Audio = "audio",
}
export enum StreamChunkField {
  Category = "category",
  Answer = "answer",
}
export interface DoubtStreamChunk {
  status: StreamChunkStatus;
  chunk_id: number;
  type?: StreamChunkType;
  field?: StreamChunkField;
  /** Text payload (when `type === Text`). For category, the literal value
   *  e.g. "academic" / "non-academic" / "gibberish" / "resolved". */
  text?: string | null;
  /** Base64-encoded audio (when `type === Audio`). */
  audio_chunk?: string;
}

// ---------------------------------------------------------------------------
// Generate-script streaming events
// ---------------------------------------------------------------------------

export interface SlideOutlineEntry {
  label: string;
  purpose?: string;
  focus: string;
}

export type GenerateScriptEvent =
  | { type: "script"; scriptId: string; label: string }
  | { type: "outline"; slides: SlideOutlineEntry[] }
  | {
      type: "slide_started";
      index: number;
      total: number;
      label: string;
      focus?: string;
    }
  | {
      type: "slide_done";
      index: number;
      total: number;
      slideId: string;
      slideLabel?: string;
    }
  | { type: "warning"; index?: number; message: string }
  | {
      type: "done";
      scriptId: string;
      slidesCreated: number;
      slidesPlanned: number;
    }
  | { type: "error"; message: string };

/**
 * Generic NDJSON streamer. POSTs `body` and yields one parsed JSON object per
 * line of the response. Keeps the connection alive until the server closes
 * it; tolerant of malformed lines (skips them) and partial trailing frames.
 *
 * Used for both `/ai/answer-doubt/stream` and `/ai/generate-script/stream`.
 */
async function* streamNdjson<T>(
  path: string,
  body: Record<string, unknown>,
  options?: { signal?: AbortSignal },
): AsyncGenerator<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...NGROK_HEADERS,
    },
    body: JSON.stringify(body),
    cache: "no-store",
    signal: options?.signal,
  });

  if (!res.ok || !res.body) {
    let message = `API ${res.status}`;
    try {
      const data = (await res.json()) as { message?: string };
      if (data?.message) message = data.message;
    } catch {
      /* fall through to status code */
    }
    throw new ApiError(res.status, message);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // NDJSON: one JSON object per line. Drain every complete line and
      // keep the trailing partial fragment for the next iteration.
      let nl: number;
      while ((nl = buffer.indexOf("\n")) !== -1) {
        const line = buffer.slice(0, nl).trim();
        buffer = buffer.slice(nl + 1);
        if (!line) continue;
        try {
          yield JSON.parse(line) as T;
        } catch {
          // Skip malformed line — keep the stream alive.
        }
      }
    }

    // Drain any final partial line that didn't end in \n.
    const tail = buffer.trim();
    if (tail) {
      try {
        yield JSON.parse(tail) as T;
      } catch {
        /* ignore */
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* noop */
    }
  }
}
